import { useEffect, useState } from "react";
import api, { API_BASE } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { Bot, Send, Save, Sparkles, Loader2 } from "lucide-react";

const FIELDS = [
  { k: "name", label: "Name", type: "input" },
  { k: "role", label: "Role", type: "input" },
  { k: "personality", label: "Personality", type: "textarea" },
  { k: "tone", label: "Tone", type: "input" },
  { k: "language", label: "Language", type: "input" },
  { k: "instructions", label: "Instructions", type: "textarea" },
  { k: "objectives", label: "Objectives", type: "textarea" },
  { k: "escalation_rules", label: "Escalation Rules", type: "textarea" },
];

const inputCls =
  "w-full bg-white border border-[#E9EDEF] focus:border-[#25D366] focus:ring-1 focus:ring-[#25D366] text-[#111B21] rounded-lg px-3 py-2 text-sm placeholder:text-[#8696A0] focus:outline-none transition-colors";

export default function AIAgent() {
  const [agent, setAgent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [chat, setChat] = useState([]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [configuring, setConfiguring] = useState(false);
  const [logoPath, setLogoPath] = useState(null);

  useEffect(() => { api.get("/ai-agent").then((r) => setAgent(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/business-profile").then((r) => setLogoPath(r.data?.logo_path || null)).catch(() => {}); }, []);

  const aiConfigure = async () => {
    setConfiguring(true);
    try {
      const r = await api.post("/ai-agent/ai-suggest");
      const s = r.data || {};
      setAgent((a) => ({ ...a, ...Object.fromEntries(Object.entries(s).filter(([, v]) => v)) }));
      toast.success("AI drafted a configuration from your knowledge base — review & save");
    } catch (e) { toast.error(e.response?.data?.detail || "AI configuration failed"); }
    finally { setConfiguring(false); }
  };

  const removeKey = async () => {
    try {
      await api.delete("/ai-agent/api-key");
      const r = await api.get("/ai-agent");
      setAgent({ ...r.data, api_key: "" });
      toast.success("Own key removed — using platform key");
    } catch (e) { toast.error("Failed to remove key"); }
  };

  const save = async () => {    setSaving(true);
    try {
      const payload = {
        name: agent.name, role: agent.role, personality: agent.personality,
        tone: agent.tone, language: agent.language, instructions: agent.instructions,
        objectives: agent.objectives, escalation_rules: agent.escalation_rules,
        model_provider: agent.model_provider, model_name: agent.model_name,
        temperature: parseFloat(agent.temperature) || 0.7,
        reply_delay_min: parseFloat(agent.reply_delay_min) || 0,
        reply_delay_max: parseFloat(agent.reply_delay_max) || 0,
        enabled: agent.enabled,
      };
      if (agent.api_key) payload.api_key = agent.api_key;
      const r = await api.put("/ai-agent", payload);
      setAgent(r.data);
      toast.success("AI Agent saved");
    } catch (e) { toast.error("Failed to save agent"); }
    finally { setSaving(false); }
  };

  const send = async () => {
    if (!input.trim()) return;
    const msg = input.trim();
    setChat((c) => [...c, { role: "user", text: msg }]);
    setInput(""); setThinking(true);
    try {
      const r = await api.post("/ai/chat", { message: msg });
      setChat((c) => [...c, { role: "ai", text: r.data.reply }]);
    } catch (e) {
      setChat((c) => [...c, { role: "ai", text: "Error: AI not configured or request failed." }]);
    } finally { setThinking(false); }
  };

  if (!agent) return <><PageHeader eyebrow="AI Engine" title="AI Agent" /><div className="p-8 text-sm text-[#8696A0]">Loading…</div></>;

  return (
    <>
      <PageHeader
        eyebrow="AI Engine" title="AI Agent"
        actions={
          <>
            <button data-testid="ai-configure" onClick={aiConfigure} disabled={configuring}
              className="flex items-center gap-2 bg-[#E7FCE8] text-[#128C7E] border border-[#25D366]/30 hover:bg-[#DCF8C6] rounded-lg font-medium px-4 py-2 text-sm transition-colors disabled:opacity-50">
              {configuring ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} AI Configure
            </button>
            <button data-testid="ai-agent-save" onClick={save} disabled={saving}
              className="flex items-center gap-2 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-lg font-medium px-4 py-2 text-sm transition-colors disabled:opacity-50">
              <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save Agent"}
            </button>
          </>
        }
      />
      <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-8 grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-5">
          <div className="border border-[#E9EDEF] bg-white rounded-lg p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bot className="h-4 w-4 text-[#075E54]" />
                <h3 className="font-heading text-sm font-medium text-[#111B21]">Agent Configuration</h3>
              </div>
              <label className="flex items-center gap-2 text-xs text-[#54656F] cursor-pointer">
                AI Enabled
                <input type="checkbox" data-testid="ai-agent-enabled" checked={agent.enabled}
                  onChange={(e) => setAgent({ ...agent, enabled: e.target.checked })} className="accent-[#25D366]" />
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8696A0]">Model Provider</label>
                <select data-testid="ai-model-provider" value={agent.model_provider}
                  onChange={(e) => setAgent({ ...agent, model_provider: e.target.value, model_name: e.target.value === "anthropic" ? "claude-sonnet-4-6" : e.target.value === "openai" ? "gpt-5.4" : "gemini-3.1-pro-preview" })}
                  className={inputCls + " mt-1"}>
                  <option value="anthropic">Anthropic (Claude Sonnet 4.6)</option>
                  <option value="openai">OpenAI (GPT-5.4)</option>
                  <option value="gemini">Gemini (3.1 Pro)</option>
                </select>
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8696A0]">Model</label>
                <input data-testid="ai-model-name" value={agent.model_name} onChange={(e) => setAgent({ ...agent, model_name: e.target.value })} className={inputCls + " mt-1 font-mono text-[#075E54]"} />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8696A0]">Temperature</label>
                <input data-testid="ai-temperature" type="number" step="0.1" min="0" max="1" value={agent.temperature ?? 0.7}
                  onChange={(e) => setAgent({ ...agent, temperature: e.target.value })} className={inputCls + " mt-1 font-mono"} />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8696A0]">Own LLM API Key {agent.has_own_key ? "(set)" : "(optional — uses platform key)"}</label>
                <input data-testid="ai-api-key" type="password" placeholder={agent.has_own_key ? "•••••• (blank keeps current)" : "sk-…"}
                  value={agent.api_key || ""} onChange={(e) => setAgent({ ...agent, api_key: e.target.value })} className={inputCls + " mt-1 font-mono"} />
                {agent.has_own_key && (
                  <button type="button" data-testid="ai-remove-key" onClick={removeKey}
                    className="mt-1 text-[11px] text-red-600 hover:text-red-300 transition-colors">Remove own key (use platform key)</button>
                )}
              </div>
            </div>

            <div className="border-t border-[#E9EDEF] pt-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-0.5 mb-1">
                <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8696A0]">Human-like Reply Delay</label>
                <span className="font-mono text-[10px] text-[#075E54]">
                  {Number(agent.reply_delay_max) > 0
                    ? `waits ${agent.reply_delay_min || 0}–${agent.reply_delay_max || 0}s before replying`
                    : "instant (feels robotic)"}
                </span>
              </div>
              <p className="text-xs text-[#8696A0] mb-2">Makes the bot pause a random moment before answering, so chats feel more human. Set max to 0 for instant replies.</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] text-[#54656F]">Min seconds</label>
                  <input data-testid="ai-reply-delay-min" type="number" step="0.5" min="0" max="60"
                    value={agent.reply_delay_min ?? 0}
                    onChange={(e) => setAgent({ ...agent, reply_delay_min: e.target.value })}
                    className={inputCls + " mt-1 font-mono"} />
                </div>
                <div>
                  <label className="text-[11px] text-[#54656F]">Max seconds</label>
                  <input data-testid="ai-reply-delay-max" type="number" step="0.5" min="0" max="60"
                    value={agent.reply_delay_max ?? 0}
                    onChange={(e) => setAgent({ ...agent, reply_delay_max: e.target.value })}
                    className={inputCls + " mt-1 font-mono"} />
                </div>
              </div>
            </div>

            {FIELDS.map((f) => (
              <div key={f.k}>
                <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8696A0]">{f.label}</label>
                {f.type === "textarea" ? (
                  <textarea data-testid={`ai-field-${f.k}`} rows={f.k === "instructions" ? 4 : 2} value={agent[f.k] || ""}
                    onChange={(e) => setAgent({ ...agent, [f.k]: e.target.value })} className={inputCls + " mt-1"} />
                ) : (
                  <input data-testid={`ai-field-${f.k}`} value={agent[f.k] || ""}
                    onChange={(e) => setAgent({ ...agent, [f.k]: e.target.value })} className={inputCls + " mt-1"} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Test chat */}
        <div className="border border-[#E9EDEF] bg-white rounded-lg flex flex-col h-[600px]">
          <div className="px-5 py-4 border-b border-[#E9EDEF]">
            <h3 className="font-heading text-sm font-medium text-[#111B21]">Test the Agent</h3>
            <p className="text-xs text-[#8696A0]">Uses your knowledge base + business profile.</p>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3" data-testid="ai-test-chat">
            {chat.length === 0 && <p className="text-xs text-[#8696A0] text-center mt-8">Send a message to test responses.</p>}
            {chat.map((m, i) => (
              <div key={i} className={`flex items-end gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role !== "user" && (
                  logoPath ? <img src={`${API_BASE}/assets/file/${logoPath}`} alt="" className="h-6 w-6 rounded-full object-cover flex-shrink-0" />
                    : <div className="h-6 w-6 rounded-full bg-[#DCF8C6] flex items-center justify-center flex-shrink-0"><Bot className="h-3.5 w-3.5 text-[#075E54]" /></div>
                )}
                <div className={`max-w-[85%] p-3 text-sm rounded-lg border ${m.role === "user" ? "bg-[#D9FDD3] border-[#B9E9B3] text-[#111B21] rounded-tr-none" : "bg-[#F0F2F5] border-[#CBD5E1] text-[#111B21] rounded-tl-none"}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {thinking && <p className="text-xs text-[#075E54] font-mono animate-pulse">agent thinking…</p>}
          </div>
          <div className="p-3 border-t border-[#E9EDEF] flex gap-2">
            <input data-testid="ai-test-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask about mixing, courses…"
              className={inputCls} />
            <button data-testid="ai-test-send" onClick={send}
              className="bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-lg px-3 transition-colors">
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
